// Builds per-placement "ad" variation lists for the Meta single-image
// placement cards (Facebook Feed Desktop/Mobile, Instagram Feed, Instagram
// Reels) from a v4 creative object.
//
// Mirrors v1's `getAdSuggestions` (utils/helpers.js) combination algorithm —
// the real headline × body × media × destination_url cartesian product, not
// a new one — so this placement's "N variations" count/content matches what
// v1 would have produced for the same creative. Two things carried over
// deliberately, both for `Context/disabledVariations.js` compatibility
// (`variationId` is read by `expandVariationId`/`isVariationDisabled`):
//  - `mediaIdx` is the item's index in the *full* `creative.media` array
//    (continuing into `placement_media`, see `indexedMediaByShape` below),
//    not the filtered feed/reel subset.
//  - Reels variations drop the headline dimension and use v1's `x`-prefixed
//    variationId (`x${bodyIdx}${mediaIdx}${urlIdx}`) — `ReelsAdCard` never
//    renders a headline, so cartesian-ing over it would just render N
//    identical-looking cards per real reel variation. `disabledVariations.js`
//    already has this `x`-prefix handling built in for exactly this reason.
//
// Feed vs Reels is decided by **aspect ratio**, matching v1's `ratio <= 1.4`
// split (`utils/helpers.js`'s `getAdSuggestions`) — not by MIME `type`. Type
// only decides `<video>` vs `<img>` rendering inside the card components.

import { resolveCallToAction } from 'utils/helpers'
import { humanizeCallToAction } from './format'

const isYoutubeMedia = (media) => Boolean(media?.youtube_video_id)

// v1 parity constant — ratio (height/width) <= 1.4 is Feed-shaped
// (square/landscape), > 1.4 is Reel-shaped (portrait).
const FEED_MAX_RATIO = 1.4

// Prefers an explicit `ratio` field (some callers precompute it, e.g. from a
// real decoded image/video's intrinsic dimensions) and falls back to
// deriving it from `width`/`height`, which is what a real v4 media item
// carries (`mediaItemSchema`). Matches v1's `undefined <= 1.4` → falsy
// behavior when neither is available (falls through to Reel).
function mediaRatio(media) {
  if (typeof media?.ratio === 'number') return media.ratio
  if (media?.width && media?.height) return media.height / media.width
  return undefined
}

const isFeedShaped = (media) => mediaRatio(media) <= FEED_MAX_RATIO

// Indexes `creative.media` first, then continues the index count into
// `creative.placement_media` — the distinct field v1 reads for reel-oriented
// assets added via a reel-first upload flow (`placement_group_id`-paired
// with a `media` entry). Continuing the index (rather than restarting at 0)
// keeps a placement_media-sourced item's `mediaIdx` from colliding with a
// real `creative.media` index in the variationId digit string. A YouTube
// passthrough item is dropped unconditionally: Meta only ever accepts a
// real uploaded video file, never a YouTube reference. No fallback to a
// wrong-shaped item any more — a placement with nothing matching its shape
// renders no cards for that item, matching how the Google adapters already
// behave.
function indexedMediaByShape(creative, wantFeedShaped) {
  const mediaList = creative?.media ?? []
  const placementMediaList = creative?.placement_media ?? []
  const indexed = [
    ...mediaList.map((media, mediaIdx) => ({ media, mediaIdx })),
    ...placementMediaList.map((media, idx) => ({ media, mediaIdx: mediaList.length + idx })),
  ]
  return indexed
    .filter(({ media }) => !isYoutubeMedia(media))
    .filter(({ media }) => isFeedShaped(media) === wantFeedShaped)
}

function nonEmpty(list) {
  return (list ?? []).filter(Boolean)
}

// Feed placements (Facebook Feed Desktop/Mobile, Instagram Feed): full
// headline × body × destination_url × media cartesian product.
export function getFeedAdVariations(creative, t) {
  const headlines = nonEmpty(creative?.headline)
  const bodies = nonEmpty(creative?.body)
  const destinationUrls = nonEmpty(creative?.destination_url)
  const mediaItems = indexedMediaByShape(creative, true)
  const callToAction = humanizeCallToAction(resolveCallToAction(creative?.call_to_action, 'fb'), t)

  const ads = []
  headlines.forEach((headline, headlineIdx) => {
    bodies.forEach((body, bodyIdx) => {
      destinationUrls.forEach((destinationUrl, urlIdx) => {
        mediaItems.forEach(({ media, mediaIdx }) => {
          ads.push({
            pageName: creative?.page_name ?? null,
            profilePictureUrl: creative?.profile_picture_url ?? null,
            headline,
            body,
            callToAction,
            destinationUrl,
            media,
            variationId: `${headlineIdx + 1}${bodyIdx + 1}${mediaIdx + 1}${urlIdx + 1}`,
          })
        })
      })
    })
  })
  return ads
}

// Reels: body × destination_url × media cartesian product — no headline
// dimension, see module note above.
export function getReelsAdVariations(creative, t) {
  const bodies = nonEmpty(creative?.body)
  const destinationUrls = nonEmpty(creative?.destination_url)
  const mediaItems = indexedMediaByShape(creative, false)
  const callToAction = humanizeCallToAction(resolveCallToAction(creative?.call_to_action, 'fb'), t)

  const ads = []
  bodies.forEach((body, bodyIdx) => {
    destinationUrls.forEach((destinationUrl, urlIdx) => {
      mediaItems.forEach(({ media, mediaIdx }) => {
        ads.push({
          pageName: creative?.page_name ?? null,
          profilePictureUrl: creative?.profile_picture_url ?? null,
          body,
          callToAction,
          destinationUrl,
          media,
          variationId: `x${bodyIdx + 1}${mediaIdx + 1}${urlIdx + 1}`,
        })
      })
    })
  })
  return ads
}
